import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import strings from "translate/language";
import { handleDetailResponse, isEmpty, normalizeMongoId, seo } from "utils/utils";
import {
  getAppointmentDetail,
  addAppointment,
} from "../../reduxStore/action/appointmentActions";
import {
  getSiteVisitStatusData,
  updateSiteVisitStatus,
} from "../../reduxStore/action/siteVisitActions";
import Breadcrumb from "component/common/breadcrumb";
import AppointmentScheduleFields from "./AppointmentScheduleFields";
import { errorToast } from "utils/httpClient";
import "./AppointmentListing.css";

const displayValue = (value) => {
  if (value == null || value === "" || value === "NA") {
    return strings.notavailable;
  }
  return value;
};

const Revisit = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const [formVaild, setFormVaild] = useState(null);
  const [form, setForm] = useState({});
  const [appointmentData, setAppointmentData] = useState({});
  const [siteVisitMapping, setSiteVisitMapping] = useState(null);
  const { response: res } = useSelector((state) => state.login);
  const { appointment_id = "" } = params || {};

  const addAppointmentSelector = useSelector((state) => state.appointment);

  useEffect(() => {
    dispatch(getAppointmentDetail({ appointment_id }));
  }, [appointment_id, dispatch]);

  useEffect(() => {
    if (addAppointmentSelector.response) {
      const { response } = addAppointmentSelector;
      if (response?.status === 200) {
        setAppointmentData(handleDetailResponse(response?.data));
      } else {
        setAppointmentData({});
      }
    }
  }, [addAppointmentSelector]);

  useEffect(() => {
    if (appointment_id) {
      dispatch(getSiteVisitStatusData({ appointment_id }))
        .then((mappingRes) => {
          if (mappingRes?.data?.length > 0) {
            setSiteVisitMapping(mappingRes.data[0]);
          }
        })
        .catch(() => {});
    }
  }, [appointment_id, dispatch]);

  useEffect(() => {
    if (addAppointmentSelector.create === true) {
      if (addAppointmentSelector?.addResponse?.status === 200) {
        if (siteVisitMapping?.jv_site_visit_id) {
          const payload = {
            jvSiteVisitId: siteVisitMapping.jv_site_visit_id,
            status: "Not Visited",
            closingStatus: "Revisit",
            role: res?.data?.role_title || "auto",
          };
          dispatch(updateSiteVisitStatus(payload))
            .catch(() => {})
            .finally(() => {
              setForm({});
              navigate("/appointment");
            });
        } else {
          setForm({});
          navigate("/appointment");
        }
      }
    }
  }, [addAppointmentSelector, dispatch, navigate, res?.data?.role_title, siteVisitMapping]);

  const handleSubmit = () => {
    if (!handleValid()) {
      const payload = {
        lead_id: normalizeMongoId(appointmentData.lead_id),
        property_id: normalizeMongoId(appointmentData.property_id),
        appointment_id,
        appointment_date: form.appointment_date,
        appointment_time: form.appointment_time,
        remark: String(form.remark || "").trim(),
        type: 1,
        update_type: 1,
        // Same as app 4-button revisit: create disposition on the appointment being closed.
        deposition_source: "appointment_details",
      };
      if (appointmentData.pickup) {
        payload.pickup = appointmentData.pickup;
      }
      if (appointmentData.pickup_location) {
        payload.pickup_location = appointmentData.pickup_location;
      }
      if (appointmentData.pickup_address) {
        payload.pickup_address = appointmentData.pickup_address;
      }
      if (appointmentData.pickup_longitude) {
        payload.pickup_longitude = appointmentData.pickup_longitude;
      }
      if (appointmentData.pickup_latitude) {
        payload.pickup_latitude = appointmentData.pickup_latitude;
      }
      dispatch(addAppointment(payload));
    }
  };

  const handleValid = () => {
    const err = {};
    let formError = false;
    if (!form.appointment_date) {
      formError = true;
      err.appointment_date = strings.followup_dateErr;
    }
    if (!form.appointment_time) {
      formError = true;
      err.appointment_time = strings.followup_timeErr;
    }
    const remarkText = String(form.remark || "").trim();
    if (!remarkText) {
      formError = true;
      err.remark =
        strings.revisitRemarkRequired ||
        "Please enter a remark for this revisit.";
    } else if (remarkText.length < 20) {
      formError = true;
      err.remark =
        strings.revisitRemarkMinLength ||
        "Remark must be at least 20 characters.";
    }
    setFormVaild(formError ? err : null);
    if (formError) {
      const firstMessage =
        err.remark || err.appointment_date || err.appointment_time;
      if (firstMessage) {
        errorToast(firstMessage);
      }
    }
    return formError;
  };

  useEffect(() => {
    seo({
      title: "Add Appointment",
      metaDescription: "Add appointment details",
      canonicalLink: window.location.href,
    });
  }, []);

  const visitorName = `${appointmentData?.customer_first_name || ""} ${
    appointmentData?.customer_last_name || ""
  }`.trim();
  const selectedDate = form.appointment_date
    ? moment(form.appointment_date, "YYYY-MM-DD").toDate()
    : null;

  return (
    <div className="page-body justo-appointment-page">
      <Breadcrumb
        title={strings.revisit}
        url={"/appointment"}
        parentListing={strings.appointmentList}
        titleactive={strings.addAppointment}
        isBackBtn={true}
      />
      <div className="container-fluid justo-appointment">
        <div className="justo-appointment__card justo-appointment-form">
          <div className="justo-appointment-form__top">
            <div className="justo-appointment__title-block">
              <h1 className="justo-appointment__title">
                {strings.addAppointment}
              </h1>
              <p className="justo-appointment__subtitle">
                {strings.revisitAppointmentSubtitle}
              </p>
            </div>
            <div className="justo-appointment-form__top-actions">
              <button
                className="justo-appointment__btn justo-appointment__btn--ghost"
                type="button"
                onClick={() => navigate("/appointment")}
              >
                {strings.cancel}
              </button>
              <button
                className="justo-appointment__btn justo-appointment__btn--solid"
                type="button"
                onClick={handleSubmit}
              >
                <i className="fa fa-check" aria-hidden="true" />
                {strings.submit}
              </button>
            </div>
          </div>
          <div className="justo-appointment-form__meta">
            <div className="justo-appointment-form__fact">
              <span className="justo-appointment-form__fact-label">
                {strings.propertyname}
              </span>
              <span className="justo-appointment-form__fact-value">
                {displayValue(appointmentData?.property_title)}
              </span>
            </div>
            <div className="justo-appointment-form__fact">
              <span className="justo-appointment-form__fact-label">
                {strings.visitorName}
              </span>
              <span className="justo-appointment-form__fact-value">
                {displayValue(visitorName)}
              </span>
            </div>
            <div className="justo-appointment-form__fact">
              <span className="justo-appointment-form__fact-label">
                {strings.AppointmentDate}
              </span>
              <span className="justo-appointment-form__fact-value">
                {appointmentData?.appointment_date
                  ? moment(appointmentData.appointment_date).format("DD MMM YYYY")
                  : strings.notavailable}
              </span>
            </div>
          </div>
          <div className="justo-appointment-form__body">
            <div className="justo-appointment-form__panel justo-appointment-form__panel--stack">
              <AppointmentScheduleFields
                dateId="revisitDate"
                hourId="revisitHour"
                selectedDate={selectedDate}
                onDateChange={(date) => {
                  setForm((prev) => ({
                    ...prev,
                    appointment_date: date
                      ? moment(date).format("YYYY-MM-DD")
                      : "",
                  }));
                }}
                appointmentTime={form.appointment_time}
                onTimeChange={(appointment_time) => {
                  setForm((prev) => ({ ...prev, appointment_time }));
                  if (appointment_time && formVaild?.appointment_time) {
                    const nextErrors = { ...formVaild };
                    delete nextErrors.appointment_time;
                    setFormVaild(isEmpty(nextErrors) ? null : nextErrors);
                  }
                }}
                dateError={formVaild?.appointment_date}
                timeError={formVaild?.appointment_time}
              />
              <div className="justo-appointment-form__field justo-appointment-form__field--full">
                <label
                  className="justo-appointment-form__label"
                  htmlFor="revisitRemark"
                >
                  {strings.remarkField}
                  <span className="required-field" />
                </label>
                <textarea
                  id="revisitRemark"
                  className={`justo-appointment__input justo-appointment-form__textarea justo-appointment-form__textarea--remark${
                    formVaild?.remark ? " is-invalid" : ""
                  }`}
                  name="remark"
                  rows={6}
                  maxLength={500}
                  required
                  aria-required="true"
                  placeholder={strings.closingRemarkPlaceholder}
                  value={form.remark || ""}
                  onChange={(e) => {
                    const remark = e.target.value;
                    setForm((prev) => ({ ...prev, remark }));
                    if (formVaild?.remark) {
                      const nextErrors = { ...formVaild };
                      delete nextErrors.remark;
                      setFormVaild(isEmpty(nextErrors) ? null : nextErrors);
                    }
                  }}
                />
                {formVaild?.remark ? (
                  <p className="justo-appointment-form__field-error">
                    {formVaild.remark}
                  </p>
                ) : (
                  <p className="justo-appointment-form__hint">
                    {strings.revisitRemarkHint}
                  </p>
                )}
              </div>
            </div>
            {!isEmpty(formVaild) && (
              <p className="justo-appointment-form__error-banner">
                {strings.fieldRequired}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Revisit;
